var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from "react";
import FetchHelper from "./lib/fetchHelper.js";
import { disablePush, enablePush, getDeviceState, isIosOutsideHomeScreen } from "./lib/pushNotifications";
const MESSAGE_TYPE_LABELS = {
    shiftacquire_substitute: "Din vagt er overtaget",
    shiftacquire_acquirer: "Du har overtaget en vagt",
    yellowshiftapproved_member: "Gul vagt godkendt",
    shiftdeleted_member: "Vagt aflyst",
    plannermessage: "Beskeder fra vagtplanlæggerne"
};
/**
 * Lets the member turn push notifications on or off for this device, and choose SMS and/or push
 * per message type. At least one channel must stay chosen for every type.
 */
export class NotificationPreferencesComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            device: "loading",
            deviceBusy: false,
            serverHasSubscription: false,
            preferences: [],
            saving: false
        };
    }
    componentDidMount() {
        getDeviceState().then((device) => this.setState({ device }));
        this.loadServerSubscription();
        FetchHelper.get("/bpapi/notificationpreferences")
            .then((preferences) => this.setState({ preferences }))
            .catch(() => this.setState({ error: "Kunne ikke hente dine indstillinger for beskeder" }));
    }
    loadServerSubscription() {
        FetchHelper.get("/bpapi/pushsubscription")
            .then((result) => this.setState({ serverHasSubscription: !!result.hasSubscription }))
            .catch(() => undefined);
    }
    toggleDevice(enable) {
        return __awaiter(this, void 0, void 0, function* () {
            this.setState({ deviceBusy: true, deviceError: undefined });
            try {
                const device = enable ? yield enablePush() : yield disablePush();
                this.setState({ device });
                if (enable && device === "blocked") {
                    this.setState({ deviceError: "Notifikationer er blokeret for siden. Du kan tillade dem i browserens indstillinger." });
                }
            }
            catch (error) {
                console.error(error);
                this.setState({ deviceError: enable ? "Kunne ikke slå notifikationer til" : "Kunne ikke slå notifikationer fra" });
            }
            finally {
                this.setState({ deviceBusy: false });
                this.loadServerSubscription();
            }
        });
    }
    toggleChannel(messageType, channel) {
        this.setState((state) => ({
            success: undefined,
            preferences: state.preferences.map((p) => {
                if (p.messageType !== messageType)
                    return p;
                const next = Object.assign(Object.assign({}, p), { [channel]: !p[channel] });
                // Never leave a message type without a channel.
                return next.sms || next.push ? next : p;
            })
        }));
    }
    save() {
        this.setState({ saving: true, error: undefined, success: undefined });
        FetchHelper.put("/bpapi/notificationpreferences", this.state.preferences)
            .then((preferences) => this.setState({ preferences, saving: false, success: "Dine indstillinger er gemt." }))
            .catch(() => this.setState({ saving: false, error: "Kunne ikke gemme dine indstillinger" }));
    }
    // The boards "Min side" and "Notifikationer - enhedsstatus": what this device says and the one button
    // that fits, with a note where something is in the way (blocked, or iPhone outside the home screen).
    renderDevice() {
        const { device, deviceBusy, deviceError } = this.state;
        const status = {
            loading: "Henter status…",
            unsupported: "Din browser understøtter ikke notifikationer.",
            blocked: "Notifikationer er blokeret for siden på denne enhed.",
            off: "Notifikationer er slået fra på denne enhed.",
            on: "Notifikationer er slået til på denne enhed."
        }[device];
        return (React.createElement("div", { className: "bi-pf-device" },
            React.createElement("span", { className: "bi-pf-subtitle" }, "Notifikationer p\u00E5 denne enhed"),
            !!deviceError && device !== "blocked" && React.createElement("div", { className: "bi-bv-alert bi-bv-alert-error", role: "alert" }, deviceError),
            device === "blocked"
                ? React.createElement(React.Fragment, null,
                    React.createElement("div", { className: "bi-bv-alert bi-bv-alert-error" }, status),
                    React.createElement("span", { className: "bi-pf-hint" }, "Du kan tillade dem i browserens indstillinger."))
                : React.createElement("span", { className: "bi-pf-status" }, status),
            device === "off" &&
                React.createElement("button", { type: "button", className: "bi-btn bi-btn-primary", disabled: deviceBusy, onClick: () => this.toggleDevice(true) }, deviceBusy ? React.createElement("i", { className: "fas fa-cog fa-spin" }) : React.createElement("span", null, "Sl\u00E5 til")),
            device === "on" &&
                React.createElement("button", { type: "button", className: "bi-btn bi-btn-outline", disabled: deviceBusy, onClick: () => this.toggleDevice(false) }, deviceBusy ? React.createElement("i", { className: "fas fa-cog fa-spin" }) : React.createElement("span", null, "Sl\u00E5 fra")),
            isIosOutsideHomeScreen() &&
                React.createElement("div", { className: "bi-bv-alert bi-bv-alert-warn" }, "P\u00E5 iPhone og iPad kan du kun f\u00E5 notifikationer, hvis du f\u00F8rst f\u00F8jer siden til din hjemmesk\u00E6rm (Del \u2192 F\u00F8j til hjemmesk\u00E6rm) og \u00E5bner den derfra. Det kr\u00E6ver iOS 16.4 eller nyere.")));
    }
    render() {
        const { preferences, saving, error, success, serverHasSubscription } = this.state;
        const pushChosen = preferences.some((p) => p.push);
        return (React.createElement("div", { className: "bi-card bi-pf-card" },
            React.createElement("h2", null, "Beskeder og notifikationer"),
            this.renderDevice(),
            React.createElement("span", { className: "bi-pf-subtitle" }, "S\u00E5dan vil jeg have mine beskeder"),
            pushChosen && !serverHasSubscription &&
                React.createElement("div", { className: "bi-bv-alert bi-bv-alert-warn" }, "Du har ingen enheder med notifikationer sl\u00E5et til, s\u00E5 du f\u00E5r en SMS i stedet."),
            React.createElement("table", { className: "bi-pf-table" },
                React.createElement("thead", null,
                    React.createElement("tr", null,
                        React.createElement("th", null, "Besked"),
                        React.createElement("th", { className: "bi-pf-col-sms" }, "SMS"),
                        React.createElement("th", { className: "bi-pf-col-push" }, "Notifikation"))),
                React.createElement("tbody", null, preferences.map((p) => {
                    var _a, _b, _c;
                    return (React.createElement("tr", { key: p.messageType },
                        React.createElement("td", null, (_a = MESSAGE_TYPE_LABELS[p.messageType]) !== null && _a !== void 0 ? _a : p.messageType),
                        React.createElement("td", null,
                            React.createElement("label", null,
                                React.createElement("input", { type: "checkbox", "aria-label": `SMS: ${(_b = MESSAGE_TYPE_LABELS[p.messageType]) !== null && _b !== void 0 ? _b : p.messageType}`, checked: p.sms, disabled: p.sms && !p.push, onChange: () => this.toggleChannel(p.messageType, "sms") }))),
                        React.createElement("td", null,
                            React.createElement("label", null,
                                React.createElement("input", { type: "checkbox", "aria-label": `Notifikation: ${(_c = MESSAGE_TYPE_LABELS[p.messageType]) !== null && _c !== void 0 ? _c : p.messageType}`, checked: p.push, disabled: p.push && !p.sms, onChange: () => this.toggleChannel(p.messageType, "push") })))));
                }))),
            React.createElement("p", { className: "bi-pf-hint" }, "Mindst \u00E9n m\u00E5de skal v\u00E6re valgt for hver besked. Engangskoder og velkomstbeskeder kommer altid som SMS. Beskeder fra vagtplanl\u00E6ggerne, som du kan svare p\u00E5, kommer ogs\u00E5 altid som SMS."),
            !!error && React.createElement("div", { className: "bi-bv-alert bi-bv-alert-error", role: "alert" }, error),
            !!success && React.createElement("div", { className: "bi-bv-alert bi-bv-alert-ok", role: "status" }, success),
            React.createElement("div", { className: "bi-pf-actions" },
                React.createElement("button", { type: "button", className: "bi-btn bi-btn-save", disabled: saving || preferences.length === 0, onClick: () => this.save() }, saving ? React.createElement("i", { className: "fas fa-cog fa-spin" }) : React.createElement("span", null, "Gem")))));
    }
}
